import type { MaterialKey } from "./types";

// MeshPhysicalMaterial preset props per finish. The customer sees a believable material;
// this is look only and never affects printability.
export interface MaterialPreset {
  label: string;
  color: string;
  roughness: number;
  metalness: number;
  clearcoat?: number;
  clearcoatRoughness?: number;
  transmission?: number;
  thickness?: number;
  ior?: number;
  opacity?: number;
  transparent?: boolean;
}

export const MATERIAL_PRESETS: Record<MaterialKey, MaterialPreset> = {
  pla: { label: "PLA (matte)", color: "#d94f4f", roughness: 0.85, metalness: 0.0 },
  petg: {
    label: "PETG (glossy)",
    color: "#2f9e8f",
    roughness: 0.25,
    metalness: 0.0,
    clearcoat: 0.6,
    clearcoatRoughness: 0.2,
  },
  abs: { label: "ABS", color: "#3a3f4b", roughness: 0.6, metalness: 0.0 },
  resin: {
    label: "Resin (translucent)",
    color: "#9fb7d4",
    roughness: 0.1,
    metalness: 0.0,
    transmission: 0.85,
    thickness: 4,
    ior: 1.5,
    clearcoat: 1,
  },
  wood: { label: "Wood-fill", color: "#9c6b3f", roughness: 0.95, metalness: 0.0 },
  metallic: { label: "Metallic-fill", color: "#b8bcc4", roughness: 0.35, metalness: 0.9 },
  transparent: {
    label: "Transparent",
    color: "#cfe6ff",
    roughness: 0.05,
    metalness: 0.0,
    transmission: 0.95,
    thickness: 2,
    ior: 1.45,
    opacity: 1,
    transparent: true,
  },
};

// Map a backend material id (PLA, PETG, ABS, RESIN_STANDARD, WOOD_FILL, METAL_FILL, and
// PLA variants like PLA_SILK) onto a preview finish key. Unknown ids fall back to PLA.
export function materialKey(id: string | null | undefined): MaterialKey {
  const v = (id ?? "").toUpperCase();
  if (v.includes("RESIN")) return "resin";
  if (v.includes("WOOD")) return "wood";
  if (v.includes("METAL")) return "metallic";
  if (v.startsWith("ABS") || v.startsWith("ASA")) return "abs";
  if (v.startsWith("PETG")) return "petg";
  return "pla";
}

export function materialColor(id: string | null | undefined): string {
  return MATERIAL_PRESETS[materialKey(id)].color;
}
